import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import DetailedInsightsModal from "@/components/DetailedInsightsModal";
import { StreamHeader } from "./stream/StreamHeader";
import { StreamCard, StreamQuestionCard, StreamEmptyState } from "./stream/StreamCard";
import { ArchiveView } from "./stream/ArchiveView";
import { useStreamFeed } from "./stream/useStreamFeed";

const FILTERS = {
  all: () => true,
  archive: () => false,
  news: (u) => u.priority === "news",
  research: (u) => u.priority === "research",
  leads: (u) => u.priority === "prospect",
  actions: (u) => ["strategy", "competitor"].includes(u.priority),
};

const HeadsUpDisplay = ({ userId, onRefresh, externalUpdates, setExternalUpdates, externalPaused, setExternalPaused }) => {
  const [localUpdates, setLocalUpdates] = useState([]);
  const [localPaused, setLocalPaused] = useState(false);
  const updates = externalUpdates !== undefined ? externalUpdates : localUpdates;
  const setUpdates = setExternalUpdates !== undefined ? setExternalUpdates : setLocalUpdates;
  const isPaused = externalPaused !== undefined ? externalPaused : localPaused;
  const setIsPaused = setExternalPaused !== undefined ? setExternalPaused : setLocalPaused;

  const [activeFilter, setActiveFilter] = useState("all");
  const [archiveSearch, setArchiveSearch] = useState("");
  const [selected, setSelected] = useState(null); // { update, index }

  const feed = useStreamFeed({ userId, updates, setUpdates, isPaused });

  const activeUpdates = updates.filter((u) => !u.isArchived);
  const filteredUpdates = activeUpdates.filter(FILTERS[activeFilter] || (() => true));
  const counts = {
    all: activeUpdates.length,
    news: activeUpdates.filter(FILTERS.news).length,
    research: activeUpdates.filter(FILTERS.research).length,
    leads: activeUpdates.filter(FILTERS.leads).length,
    actions: activeUpdates.filter(FILTERS.actions).length,
  };

  const handlePauseToggle = () => {
    setIsPaused((prev) => !prev);
    if (isPaused) toast.success("Autonomous stream resumed");
    else toast.info("Autonomous stream paused");
  };

  const handleReview = (update, index) => {
    feed.markReviewed(update, index);
    setSelected({ update, index });
  };

  return (
    <motion.div
      className="glass-card p-6 h-[80vh] min-h-[500px] flex flex-col relative overflow-hidden"
      data-testid="heads-up-display"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div className="absolute inset-0 pointer-events-none" />

      <StreamHeader feed={feed} isPaused={isPaused} onPauseToggle={handlePauseToggle} activeFilter={activeFilter} setActiveFilter={setActiveFilter} counts={counts} />

      {activeFilter === "archive" ? (
        <ArchiveView userId={userId} active search={archiveSearch} setSearch={setArchiveSearch} />
      ) : (
        <ScrollArea className="flex-1 min-h-0 pr-4 mb-2 relative z-10" data-testid="stream-scroll-area">
          <div className="space-y-4">
            <AnimatePresence>
              {filteredUpdates.length > 0 ? filteredUpdates.map((update, idx) => (
                update.type === "question" && update.question && update.status !== "answered"
                  ? <StreamQuestionCard key={update.questionId || update.activityId || `question-${idx}`} update={update} idx={idx} userId={userId} onAnswered={feed.markAnswered} />
                  : <StreamCard key={update.activityId || idx} update={update} idx={idx} userId={userId} onReview={handleReview} />
              )) : (
                <StreamEmptyState activeFilter={activeFilter} />
              )}
            </AnimatePresence>
          </div>
        </ScrollArea>
      )}

      <DetailedInsightsModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        activityData={selected?.update.fullData}
        userId={userId}
        onStrategyExecuted={() => selected && feed.markExecuted(selected.update, selected.index)}
      />
    </motion.div>
  );
};

export default HeadsUpDisplay;
